import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";
import { StreamableHTTPClientTransport } from "@modelcontextprotocol/sdk/client/streamableHttp.js";
import { UnauthorizedError } from "@modelcontextprotocol/sdk/client/auth.js";
import type {
  JSONRPCMessage,
  JSONRPCResponse,
} from "@modelcontextprotocol/sdk/types.js";
import z from "zod";

import packageJson from "../../package.json" with { type: "json" };
import { FileOAuthProvider } from "./oauth-provider.js";

const DEFAULT_REMOTE_URL = "https://mcp.zeroheight.com/mcp";
const initializeRequestSchema = z.object({
  method: z.literal("initialize"),
  params: z.object({
    protocolVersion: z.string(),
  }),
});

function buildUserAgent(client: string | undefined): string {
  const baseUserAgent = `zeroheight-local-mcp/${packageJson.version}`;
  const trimmedClient = client?.trim();
  return trimmedClient ? `${baseUserAgent} ${trimmedClient}` : baseUserAgent;
}

export function buildLocalMcpRequestHeaders(
  env: NodeJS.ProcessEnv,
): Record<string, string> {
  const headers: Record<string, string> = {
    "User-Agent": buildUserAgent(env["ZEROHEIGHT_MCP_CLIENT"]),
  };
  const apiClient = env["ZEROHEIGHT_CLIENT_ID"];
  const apiKey = env["ZEROHEIGHT_ACCESS_TOKEN"];
  if (apiClient && apiKey) {
    headers["X-API-CLIENT"] = apiClient;
    headers["X-API-KEY"] = apiKey;
  }
  return headers;
}

export interface RemoteProxyOptions {
  remoteUrl?: string;
  env?: NodeJS.ProcessEnv;
  stdinTransport?: StdioServerTransport;
  remoteTransport?: ProxyRemoteTransport;
  oauthProvider?: FileOAuthProvider;
}

export interface ProxyRemoteTransport {
  onmessage?: (message: JSONRPCMessage) => void;
  onerror?: (error: Error) => void;
  onclose?: () => void;
  start(): Promise<void>;
  close(): Promise<void>;
  send(message: JSONRPCMessage): Promise<void>;
  finishAuth(authorizationCode: string): Promise<void>;
  setProtocolVersion(version: string): void;
}

export async function runRemoteProxy(options: RemoteProxyOptions = {}) {
  const env = options.env ?? process.env;
  const remoteUrl = new URL(
    options.remoteUrl ?? env["ZEROHEIGHT_MCP_REMOTE_URL"] ?? DEFAULT_REMOTE_URL,
  );
  const stdio = options.stdinTransport ?? new StdioServerTransport();
  const apiClient = env["ZEROHEIGHT_CLIENT_ID"];
  const apiKey = env["ZEROHEIGHT_ACCESS_TOKEN"];
  const oauthProvider =
    apiClient && apiKey
      ? undefined
      : (options.oauthProvider ?? new FileOAuthProvider());

  const remote =
    options.remoteTransport ??
    new StreamableHTTPClientTransport(remoteUrl, {
      authProvider: oauthProvider,
      requestInit: {
        headers: buildLocalMcpRequestHeaders(env),
      },
    });

  remote.onmessage = (message) => {
    stdio.send(message).catch((error) => remote.onerror?.(error));
  };
  remote.onerror = (error) => console.error("Remote MCP error:", error);
  remote.onclose = () => stdio.close().catch(() => {});

  stdio.onmessage = async (message) => {
    if (isInitializeRequest(message)) {
      remote.setProtocolVersion(message.params.protocolVersion);
    }

    try {
      await sendRemote(remote, message, oauthProvider);
    } catch (error) {
      await sendJsonRpcError(stdio, message, error);
    }
  };
  stdio.onerror = (error) => console.error("STDIO MCP error:", error);
  stdio.onclose = () => remote.close().catch(() => {});

  await remote.start();
  await stdio.start();

  console.error(
    apiClient && apiKey
      ? "zeroheight local MCP connected with API-key auth"
      : "zeroheight local MCP connected with OAuth auth",
  );
}

async function sendRemote(
  remote: ProxyRemoteTransport,
  message: JSONRPCMessage,
  oauthProvider: FileOAuthProvider | undefined,
) {
  try {
    await remote.send(message);
  } catch (error) {
    if (!oauthProvider || !(error instanceof UnauthorizedError)) {
      throw error;
    }

    const authorizationCode = await oauthProvider.waitForAuthorizationCode();
    await remote.finishAuth(authorizationCode);
    await remote.send(message);
  }
}

async function sendJsonRpcError(
  stdio: StdioServerTransport,
  message: JSONRPCMessage,
  error: unknown,
) {
  for (const id of getRequestIds(message)) {
    await stdio.send({
      jsonrpc: "2.0",
      id,
      error: {
        code: -32000,
        message:
          error instanceof Error ? error.message : "Remote MCP request failed",
      },
    } satisfies JSONRPCResponse);
  }
}

function getRequestIds(message: JSONRPCMessage): Array<string | number> {
  if (Array.isArray(message)) {
    return message.flatMap(getRequestIds);
  }
  if ("id" in message && typeof message.id !== "undefined") {
    return [message.id];
  }
  return [];
}

function isInitializeRequest(
  message: JSONRPCMessage,
): message is JSONRPCMessage & {
  method: "initialize";
  params: { protocolVersion: string };
} {
  return initializeRequestSchema.safeParse(message).success;
}
